import React, { useState } from 'react';
import { Room, UserProfile, CreateRoomData } from '@shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { EditablePosition } from '../admin/EditablePosition';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import { collection, doc, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

interface RoomsTabProps {
  rooms: Room[];
  users: UserProfile[];
}

export function RoomsTab({ rooms, users }: RoomsTabProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleUpdateRoom = async (roomId: string, roomData: Partial<Omit<Room, 'admins'>>) => {
    try {
      setLoading(true);
      setError(null);
      
      const manageRoom = httpsCallable(functions, 'manageRoom');
      const result = await manageRoom({
        action: 'update',
        roomId,
        roomData
      });
      
      alert((result.data as { message: string }).message);
      
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update room');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoom = async (roomId: string, roomName: string) => {
    if (!confirm(`Are you sure you want to delete room "${roomName}"? This will:
- Remove all tables and waitlists
- Delete any uploaded logos
- Unassign all admins from this room
This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const manageRoom = httpsCallable(functions, 'manageRoom');
      const result = await manageRoom({
        action: 'delete',
        roomId
      });
      
      alert((result.data as { message: string }).message);
      
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete room');
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayOrderChange = async (roomId: string, newPosition: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all rooms to calculate positions
      const roomsRef = collection(db, 'rooms');
      const roomsSnapshot = await getDocs(roomsRef);
      const allRooms = roomsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Room[];
      
      // Sort by current display_order
      const sortedRooms = [...allRooms].sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
      
      // Find the room being moved
      const movingRoom = sortedRooms.find(room => room.id === roomId);
      if (!movingRoom) {
        throw new Error('Room not found');
      }
      
      const oldPosition = movingRoom.display_order || 999;
      
      // If moving to same position, do nothing
      if (oldPosition === newPosition) {
        setLoading(false);
        return;
      }
      
      // Create batch to update all affected rooms
      const batch = writeBatch(db);
      
      if (newPosition < oldPosition) {
        // Moving up: shift rooms between new and old position down
        for (const room of sortedRooms) {
          const roomDisplayOrder = room.display_order || 999;
          if (roomDisplayOrder >= newPosition && roomDisplayOrder < oldPosition) {
            const roomRef = doc(db, 'rooms', room.id);
            batch.update(roomRef, { 
              display_order: roomDisplayOrder + 1,
              updated_at: serverTimestamp()
            });
          }
        }
      } else {
        // Moving down: shift rooms between old and new position up
        for (const room of sortedRooms) {
          const roomDisplayOrder = room.display_order || 999;
          if (roomDisplayOrder > oldPosition && roomDisplayOrder <= newPosition) {
            const roomRef = doc(db, 'rooms', room.id);
            batch.update(roomRef, { 
              display_order: roomDisplayOrder - 1,
              updated_at: serverTimestamp()
            });
          }
        }
      }
      
      // Update the moved room's display_order
      const movingRoomRef = doc(db, 'rooms', roomId);
      batch.update(movingRoomRef, { 
        display_order: newPosition,
        updated_at: serverTimestamp()
      });
      
      await batch.commit();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update display order');
    } finally {
      setLoading(false);
    }
  };

  const getAdminNames = (adminIds: string[]) => {
    if (!adminIds || adminIds.length === 0) return 'No admins assigned';
    
    const adminNames = adminIds.map(adminId => {
      const admin = users.find(user => user.uid === adminId);
      return admin ? admin.name : `Unknown (${adminId})`;
    });
    
    return adminNames.join(', ');
  };

  return (
    <ErrorBoundary>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rooms Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage poker rooms and admin assignments
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
            Create Room
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Rooms Grid */}
        {rooms.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new poker room.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary"
              >
                Create Room
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms
              .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
              .map((room) => (
              <div key={room.id} className="card card-hover">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {room.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      ID: {room.id}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingRoom(room)}
                      className="text-primary hover:text-primary/80 text-sm"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteRoom(room.id, room.name)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Address</h4>
                    <p className="text-sm text-gray-600">{room.address}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Phone</h4>
                    <p className="text-sm text-gray-600">{room.phone}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Assigned Admins</h4>
                    <p className="text-sm text-gray-600">
                      {getAdminNames(room.admins)}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Created</h4>
                    <p className="text-sm text-gray-600">
                      {room.created_at ? new Date(room.created_at.seconds * 1000).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600">
                    Created: {room.created_at ? new Date(room.created_at.seconds * 1000).toLocaleDateString() : '-'}
                  </div>
                  <EditablePosition
                    position={room.display_order || 999}
                    maxPosition={rooms.length}
                    onUpdate={(newPosition) => handleDisplayOrderChange(room.id, newPosition)}
                    loading={loading}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Room Modal */}
        {editingRoom && (
          <EditRoomModal
            room={editingRoom}
            users={users}
            onSave={(roomId, roomData) => {
              handleUpdateRoom(roomId, roomData);
              setEditingRoom(null);
            }}
            onCancel={() => setEditingRoom(null)}
            loading={loading}
          />
        )}

        {/* Create Room Modal */}
        {showCreateForm && (
          <CreateRoomModal
            users={users}
            onSave={async (roomData) => {
              try {
                const manageRoom = httpsCallable(functions, 'manageRoom');
                const result = await manageRoom({
                  action: 'create',
                  roomData
                });
                
                alert((result.data as { message: string }).message);
                setShowCreateForm(false);
                
              } catch (err: unknown) {
                alert(err instanceof Error ? err.message : 'Failed to create room');
              }
            }}
            onCancel={() => setShowCreateForm(false)}
            loading={loading}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

// Edit Room Modal Component
interface EditRoomModalProps {
  room: Room;
  users: UserProfile[];
  onSave: (roomId: string, roomData: Partial<Omit<Room, 'admins'>>) => void;
  onCancel: () => void;
  loading: boolean;
}

function EditRoomModal({ room, users, onSave, onCancel, loading }: EditRoomModalProps) {
  const [formData, setFormData] = useState({
    name: room.name,
    address: room.address,
    phone: room.phone,
    websiteUrl: room.websiteUrl || '',
    smsEnabled: room.smsEnabled || false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(room.id, formData);
  };

  // Get assigned admins for display only
  const assignedAdmins = room.admins?.map(adminId => 
    users.find(user => user.uid === adminId)
  ).filter((admin): admin is UserProfile => admin !== undefined) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit Room: {room.name}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Room Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Website URL (Optional)</label>
            <input
              type="url"
              value={formData.websiteUrl}
              onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
              className="input"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.smsEnabled}
                onChange={(e) => setFormData({ ...formData, smsEnabled: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div>
                <span className="label">Enable SMS Notifications</span>
                <p className="text-xs text-gray-500 mt-1">
                  Send SMS messages to players when their table is ready
                </p>
              </div>
            </label>
          </div>

          <div>
            <label className="label">Assigned Admins</label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
              {assignedAdmins.length === 0 ? (
                <p className="text-sm text-gray-500">No admins assigned to this room</p>
              ) : (
                assignedAdmins.map((admin) => (
                  <div key={admin.uid} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-sm text-gray-700">
                      {admin.name} ({admin.email})
                    </span>
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Admin assignments are managed in the Users Management tab
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Room Modal Component
interface CreateRoomModalProps {
  users: UserProfile[];
  onSave: (roomData: CreateRoomData) => void;
  onCancel: () => void;
  loading: boolean;
}

function CreateRoomModal({ users, onSave, onCancel, loading }: CreateRoomModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    websiteUrl: '',
    smsEnabled: false,
    admins: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleAdminToggle = (adminId: string) => {
    setFormData(prev => ({
      ...prev,
      admins: prev.admins.includes(adminId)
        ? prev.admins.filter(id => id !== adminId)
        : [...prev.admins, adminId]
    }));
  };

  const adminUsers = users.filter(user => user.role === 'admin');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Create New Room
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Room Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Main Poker Room"
              required
            />
          </div>

          <div>
            <label className="label">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
              placeholder="e.g., 123 Casino St, Las Vegas, NV"
              required
            />
          </div>

          <div>
            <label className="label">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              placeholder="e.g., +1-555-123-4567"
              required
            />
          </div>

          <div>
            <label className="label">Website URL (Optional)</label>
            <input
              type="url"
              value={formData.websiteUrl}
              onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
              className="input"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.smsEnabled}
                onChange={(e) => setFormData({ ...formData, smsEnabled: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div>
                <span className="label">Enable SMS Notifications</span>
                <p className="text-xs text-gray-500 mt-1">
                  Send SMS messages to players when their table is ready
                </p>
              </div>
            </label>
          </div>

          <div>
            <label className="label">Assigned Admins (Optional)</label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {adminUsers.length === 0 ? (
                <p className="text-sm text-gray-500">No admin users available</p>
              ) : (
                adminUsers.map((admin) => (
                  <label key={admin.uid} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.admins.includes(admin.uid)}
                      onChange={() => handleAdminToggle(admin.uid)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">
                      {admin.name} ({admin.email})
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Create Room
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
